/**
 * PrepressReport.jsx — PR ⑬c
 *
 * What the generated dieline actually is, per tagged carton: the shape the
 * machine will cut, the body it folds into, the four flap sizes, and how the
 * input drawing was normalised to get there.
 *
 * The operator win the contract describes (§1) is being able to see the
 * dieline BEFORE the job runs, instead of trusting that a bare outline turned
 * into the right box. Every value here comes from `prepress_report`; nothing is
 * computed from the artwork client-side.
 *
 * CORRELATION: entries are matched by NAME, never by index. A pair-mode carton
 * emits two pieces but one report entry, so index lookup silently returns a
 * different carton (contract §8).
 */
import React from 'react';
import { useTheme } from './theme';
import DiePreview from './DiePreview';
import {
  reportEntryFor, bodyReadoutText, blankSummaryText, blankSummary,
  flapDims, poseNotes, blankHasFlaps, modeLabel,
  resolvedModeFor, prepressPreviewFor, previewViewBox, previewIsRenderable,
  familyOf, cartonVariant, DEFAULT_CARTON_TYPE,
} from './prepress';

function Chip({ children, tone }) {
  const { P, MO } = useTheme();
  return (
    <span style={{
      fontFamily: MO, fontSize: 9.5, padding: '2px 7px', borderRadius: 4,
      background: tone === 'warn' ? P.warnSoft : P.elev,
      color: tone === 'warn' ? P.warn : P.muted,
      border: `0.5px solid ${tone === 'warn' ? P.warn : P.ruleSoft}`,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

/**
 * One flap value, in THREE states rather than two.
 *
 * `derived` reads as LOCKED — `dust` and `bf` are formulas, and an operator who
 * reads them as editable-but-disabled will file a bug asking why they can't be
 * changed. `adjustable` shows the range the engine will honour.
 *
 * ⑭k adds the third. This cell used to print "derived" under anything that was
 * not editable, so a tuck carton's `tuck_flap` — which this deployment publishes
 * no adjustability for either way — was labelled a derived formula on the one
 * card an operator checks before plating. That is ⑭i's bottom-flap defect
 * inverted: a confident wrong claim in place of an honest gap. An `unknown` row
 * makes no claim.
 */
function FlapCell({ row }) {
  const { P, MO, FN } = useTheme();
  const hasRange = row.adjustability === 'adjustable' && row.min != null && row.max != null;
  const note = row.adjustability === 'derived' ? 'derived'
    : row.adjustability === 'adjustable' ? (hasRange ? `${row.min}–${row.max} mm` : 'adjustable')
      : 'reported, not adjustable here';
  return (
    <div style={{
      flex: '1 1 96px', minWidth: 96, padding: '7px 9px', borderRadius: 6,
      background: P.elev, border: `0.5px solid ${P.ruleSoft}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{
          fontFamily: MO, fontSize: 9, color: P.dim,
          textTransform: row.labelled ? 'uppercase' : 'none', letterSpacing: 0.4,
        }}>
          {row.label}
        </span>
        {row.adjustability === 'derived'
          && <span title="Derived from the other dimensions — not editable" style={{ fontSize: 9, color: P.dim }}>🔒</span>}
      </div>
      <div style={{
        fontFamily: MO, fontSize: 13, fontWeight: 500, marginTop: 2,
        color: row.adjustability === 'adjustable' ? P.ink : P.muted,
      }}>
        {row.valueMm} <span style={{ fontSize: 9.5, color: P.dim }}>mm</span>
      </div>
      <div style={{ fontFamily: FN, fontSize: 9.5, color: P.dim, marginTop: 2 }}>{note}</div>
    </div>
  );
}

function CartonReport({ entry, preview, mode, caps }) {
  const { P, MO, FN } = useTheme();
  const body = bodyReadoutText(entry);
  const blankText = blankSummaryText(entry);
  const blank = blankSummary(entry);
  const flaps = flapDims(entry, caps);
  const pose = poseNotes(entry);
  // ⑭k: TRI-STATE. `null` means this deployment published no unflapped corner
  // count for this family, so the check could not run — which is not the same
  // as "the flaps are missing" and must not fire that warning.
  const flapped = blankHasFlaps(entry, caps);
  const family = familyOf(entry);
  const variant = cartonVariant(entry);
  // Null preview has one meaning: this deployment's package predates `assets`,
  // so previews are omitted rather than partial. Render nothing — never
  // synthesise geometry from the nesting result (rev 5 §5.2).
  const renderable = previewIsRenderable(preview);
  const sharedBox = previewViewBox(preview);

  return (
    <div style={{
      padding: 14, borderRadius: 8, background: P.sheet,
      border: `0.5px solid ${flapped === false ? P.warn : P.ruleSoft}`,
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: MO, fontSize: 12, fontWeight: 500, color: P.ink }}>{entry.name}</span>
        {/* The mode the SERVER resolved, never the one that was requested.
            This chip used to read "prepress · plain" unconditionally, so an
            `auto` run that chose a pair tile would still have claimed plain —
            on the one card an operator checks before plating. */}
        <Chip tone={mode === 'pair' ? 'warn' : undefined}>
          {modeLabel(mode) || 'prepress · mode unreported'}
        </Chip>
        {/* ⑭k / rev 12 §1: which family this is, on the card, always. Three
            families share a report shape but not its CONTENT — `tuck_flap` is
            not BL's `tf` (§1.1) — so the reader has to be able to see which
            carton the numbers below belong to. */}
        {family !== DEFAULT_CARTON_TYPE && <Chip>{family}</Chip>}
        {/* §1.3: READ, never derived. Both real STE cartons happen to make
            12 corners mean centre_column, which is why computing it would pass
            every test and break on the first carton that disagrees. */}
        {variant && <Chip>{variant.replace(/_/g, ' ')}</Chip>}
        {(preview?.fingerprint || blank?.fingerprint)
          && <Chip>{String(preview?.fingerprint || blank.fingerprint).slice(0, 12)}</Chip>}
      </div>

      {/* Input outline → generated dieline, at a SHARED scale.
          ⑭b: both halves now come from `prepress_previews[]`, matched by name.
          The "before" used to be our own client-side detection of the uploaded
          PDF; it is now the outline the server actually worked from, so the
          two halves cannot disagree about what was submitted.
          One viewBox for both = the union bbox, which needs no offset because
          each half is anchored at its own local origin. Side by side only —
          overlaying them would mis-register the base inside the blank and look
          entirely plausible while doing it (rev 6 §5.2). */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        {renderable && (
          <>
            <figure style={{ margin: 0, textAlign: 'center' }}>
              <DiePreview vertices={preview.before.vertices} viewBox={sharedBox} size={92} />
              <figcaption style={{ fontFamily: MO, fontSize: 9, color: P.dim, marginTop: 3 }}>
                your outline · {preview.before.vertexCount} corners
              </figcaption>
            </figure>
            <span style={{ fontFamily: MO, fontSize: 16, color: P.dim, alignSelf: 'center' }}>→</span>
            <figure style={{ margin: 0, textAlign: 'center' }}>
              <DiePreview vertices={preview.after.vertices} viewBox={sharedBox} size={110} />
              <figcaption style={{ fontFamily: MO, fontSize: 9, color: P.dim, marginTop: 3 }}>
                will be cut · {preview.after.vertexCount} corners
              </figcaption>
            </figure>
          </>
        )}
        <div style={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {body && <div style={{ fontFamily: FN, fontSize: 12, color: P.ink, lineHeight: 1.45 }}>{body}</div>}
          {blankText && <div style={{ fontFamily: MO, fontSize: 10.5, color: P.muted }}>{blankText}</div>}
          {!flapped && (
            <div style={{ fontFamily: FN, fontSize: 11, color: P.warn, lineHeight: 1.45 }}>
              ⚠ This dieline still has the bare outline’s corner count — the flaps were not added. Do not send it to plate.
            </div>
          )}
          {/* Absent previews mean the deployment's prepress package predates
              `assets`, so they are omitted rather than partial. Say that,
              rather than leaving a silent gap where a dieline should be —
              but do NOT draw a substitute. */}
          {!renderable && (
            <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, lineHeight: 1.45 }}>
              No dieline preview from this server — the readouts below still describe
              what was generated.
            </div>
          )}
        </div>
      </div>

      {flaps && (
        <div>
          <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            Flap sizes
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {flaps.map((row) => <FlapCell key={row.key} row={row} />)}
          </div>
          {/* ⑭k: written from what THIS carton's rows actually say, not from
              BL's four-flap sentence. A tuck carton has no bottom flap and the
              old caption named one. */}
          <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 6, lineHeight: 1.45 }}>
            {flaps.some((r) => r.adjustability === 'derived')
              ? 'Flaps marked 🔒 are derived from the other dimensions and cannot be set directly.'
              : 'Flap adjustability is read from this server, per carton family.'}
          </div>
        </div>
      )}

      {pose && (pose.notes.length > 0 || pose.deviationUm !== null) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pose.notes.map((n) => (
            <div key={n} style={{
              fontFamily: FN, fontSize: 11, lineHeight: 1.45,
              color: pose.mirrored && n.startsWith('Drawing was mirrored') ? P.warn : P.muted,
            }}>{n}</div>
          ))}
          {pose.deviationUm !== null && (
            <div style={{ fontFamily: MO, fontSize: 9.5, color: P.dim }}>
              drawing accuracy · {pose.deviationUm} µm max deviation
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} result  layout response carrying `prepress_report` and
 *                         `prepress_previews`
 *
 * ⑭b: the bench is no longer a prop. Both halves of every preview come from
 * the response, so this component takes a preview object rather than deriving
 * geometry from the nesting result — which is the data-flow inversion rev 5
 * §5.2 asked for.
 */
export default function PrepressReport({ result, caps }) {
  const { P, MO } = useTheme();
  const entries = result?.prepress_report?.cartons;
  if (!Array.isArray(entries) || entries.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontFamily: MO, fontSize: 10, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Generated dielines
      </div>
      {entries.map((entry) => {
        // The report is the authority on WHICH cartons were processed; the
        // preview supplies the geometry. Both correlate by name — a pair
        // carton emits two pieces and one entry, so index lookup would attach
        // one carton's dieline to another.
        const safe = reportEntryFor(result, entry?.name);
        if (!safe) return null;
        return <CartonReport key={entry.name} entry={safe} caps={caps}
          preview={prepressPreviewFor(result, entry?.name)}
          mode={resolvedModeFor(result, entry?.name)} />;
      })}
    </div>
  );
}
